/*
 * The map a game is played on (chosen when it starts, kept for good): the familiar green
 * countryside, a desert, or a valley in the mountains. The land is drawn to match (the
 * client), and the weather follows it here: a desert has hot summers and mild winters, a wide
 * swing between day and night, hardly any rain or fog and no snow, but dust storms on windy
 * days; the mountains have cold winters, plenty of snow, a blizzard or two and valley fog.
 */

/** Saved (World.theme, snapshot v52): append only. */
export const MapTheme = { Temperate: 0, Desert: 1, Alpine: 2 } as const;
export type MapThemeId = (typeof MapTheme)[keyof typeof MapTheme];
export const MAP_THEMES: readonly MapThemeId[] = [0, 1, 2];

/** A theme id from anywhere (an old save, the wire): an unknown one is the temperate map. */
export function mapTheme(id: number): MapThemeId {
  return id === MapTheme.Desert || id === MapTheme.Alpine ? id : MapTheme.Temperate;
}

/** Typical mean temperature (°C) by season (spring, summer, autumn, winter) for each theme. */
const SEASON_CLIMATE_BY_THEME: readonly (readonly number[])[] = [
  [13, 24, 9, -5],
  [27, 34, 24, 16],
  [5, 15, 2, -10],
];

/** The season's typical mean temperature on this map. */
export function typicalClimate(theme: number, season: number): number {
  return SEASON_CLIMATE_BY_THEME[mapTheme(theme)]?.[season] ?? 12;
}

/** Where the day-to-day mean temperature stays on each map (°C). */
const CLIMATE_RANGE: readonly (readonly [number, number])[] = [
  [-10, 30],
  [8, 40],
  [-20, 24],
];

export function climateRange(theme: number): readonly [number, number] {
  return CLIMATE_RANGE[mapTheme(theme)] ?? [-10, 30];
}

/** How wide the day's swing is (percent of the temperate one): dry desert air, bigger. */
const DIURNAL_PERCENT: readonly number[] = [100, 150, 110];

export function diurnalPercent(theme: number): number {
  return DIURNAL_PERCENT[mapTheme(theme)] ?? 100;
}

/** Whether winter brings its blizzard (snow.ts): not in the desert. */
export function hasBlizzards(theme: number): boolean {
  return mapTheme(theme) !== MapTheme.Desert;
}

/** A dust storm rises on a windy hour (knots at least this) on the desert map. */
export const DUST_WIND = 16;
